<template>
  <section class="search-regions">
    <EScrollableContent class="search-regions__content" :scroll-shadow="false">
      <template #fixed>
        <header
          v-if="currentSearchRegionName"
          class="search-regions__selected"
          data-test-id="search-regions-current-region"
        >
          {{ currentSearchRegionName }}
          <EIcon
            class="search-regions__selected-icon"
            icon="check"
            size="s"
          />
        </header>
      </template>
      <template #scrollable>
        <EList class="search-regions__list" role="none">
          <EListItem
            v-for="searchRegion in regions"
            :key="searchRegion.value"
            :data-test-id="`search-regions-region-${searchRegion.value}`"
            class="search-regions__item"
            role="menuitem"
            @click="updateSearchRegion(searchRegion.value)"
            @keyup.enter.prevent.stop="updateSearchRegion(searchRegion.value)"
          >
            {{ searchRegion.label }}
          </EListItem>
        </EList>
      </template>
    </EScrollableContent>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { sendEvent } from '~~/common/js/client/analytics';
import { MAIN as MAIN_COOKIE } from '~~/common/js/constants/cookies';
import { getSortedLocalisedMarkets } from '~~/common/js/universal/market-utils';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';
import EScrollableContent from '~~/common/vue/components/scrollable-content';

export default {
  name: 'ESearchRegions',
  components: {
    EIcon,
    EList,
    EListItem,
    EScrollableContent,
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['bingMarketCode']),
    currentSearchRegionName() {
      const region = this.regions.find(({ value }) => value === this.bingMarketCode);
      return region ? region.label : null;
    },
    regions() {
      return getSortedLocalisedMarkets(this.locale);
    },
  },
  methods: {
    updateSearchRegion(searchRegion) {
      sendEvent('search_region_click', { searchRegion, currentRegion: this.bingMarketCode });

      const user = new EcosiaUser({
        cookieString: this.$cookieManager.get(MAIN_COOKIE),
        userAgent: navigator.userAgent,
        acceptLanguage: navigator.language,
      });

      user.set('bingMarketCode', searchRegion);
      user.set('customSettings', 1);

      const url = new URL(window.location.href);
      url.searchParams.delete('p');
      window.location.assign(url.toString());
      // it could happen that the new URL is the same as the old one
      // so we also have to reload
      window.location.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
$search-regions-list-max-height: 300px;

.search-regions__content {
  height: $search-regions-list-max-height;
  padding: 0;
  position: relative;
}

.search-regions__footer {
  @include divider('top');

  font-size: $font-m;
  max-height: $space-3l;
  padding: $space-s $space-m;
  text-align: center;

  @include desktop {
    font-size: $font-m;
  }
}

.search-regions__selected {
  @include divider('bottom');

  font-size: $font-m;
  font-weight: $font-weight-1l;
  height: $space-3l;
  margin-left: $space-s;
  margin-right: $space-s;
  padding: $space-s $space-2s;
  position: relative;

  @include desktop {
    font-size: $font-m;
    padding: $space-s $space-1s;
  }
}

.search-regions__item {
  padding-left: $space-m;
  min-height: $space-3l;

  @include desktop {
    font-size: $font-m;
    padding-left: $space-l;
  }

  &:focus {
    @extend %keyboard-nav-focus;

    background: none;
  }
}

.search-regions__selected-icon {
  position: absolute;
  top: 50%;
  right: $space-s;
  transform: translateY(-50%);
  pointer-events: none;
}
</style>
